import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

let isRegistered = false;

/**
 * Initialize and register push notifications
 * This should be called when the app starts and user is authenticated
 */
export async function initializePushNotifications(userId: string) {
  if (!Capacitor.isNativePlatform()) {
    console.log('[PushNotifications] Not native platform, skipping');
    return;
  }

  if (isRegistered) {
    console.log('[PushNotifications] Already registered');
    return;
  }

  try {
    // Check permission status
    const permissionStatus = await PushNotifications.checkPermissions();
    console.log('[PushNotifications] Permission status:', permissionStatus.receive);

    if (permissionStatus.receive === 'prompt') {
      console.log('[PushNotifications] Permission not granted yet (prompt). Waiting for onboarding.');
      return;
    } else if (permissionStatus.receive !== 'granted') {
      console.log('[PushNotifications] Permission not granted');
      return;
    }

    // Register with Firebase
    await PushNotifications.register();
    console.log('[PushNotifications] Registered with Firebase');

    // Listen for registration token
    PushNotifications.addListener('registration', async (token) => {
      console.log('[PushNotifications] Token received:', token.value);
      try {
        await (supabase.rpc as any)('save_push_token', {
          p_token: token.value,
          p_platform: Capacitor.getPlatform(),
          p_device_id: null,
          p_device_model: null,
          p_app_version: null,
        });
        console.log('[PushNotifications] Token saved to database');
      } catch (error) {
        console.error('[PushNotifications] Failed to save token:', error);
      }
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[PushNotifications] Registration error:', error);
    });

    // Listen for push notifications received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[PushNotifications] Received:', notification);
      // Show in-app notification or update badge count
      handleNotificationReceived(notification);
    });

    // Listen for notification action performed (user tapped notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[PushNotifications] Action performed:', action);
      handleNotificationAction(action);
    });

    isRegistered = true;
    console.log('[PushNotifications] Initialization complete');

  } catch (error) {
    console.error('[PushNotifications] Initialization error:', error);
  }
}

/**
 * Unregister push notifications (e.g., on logout)
 */
export async function unregisterPushNotifications(_userId: string, token?: string) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await PushNotifications.unregister();
    isRegistered = false;

    if (token) {
      try {
        await (supabase.rpc as any)('deactivate_push_token', { p_token: token });
      } catch {}
    }

    console.log('[PushNotifications] Unregistered');
  } catch (error) {
    console.error('[PushNotifications] Unregister error:', error);
  }
}

/**
 * Get delivered notifications (for badge count)
 */
export async function getDeliveredNotifications() {
  if (!Capacitor.isNativePlatform()) return [];
  
  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  } catch (error) {
    console.error('[PushNotifications] Get delivered error:', error);
    return [];
  }
}

/**
 * Remove delivered notifications
 */
export async function removeDeliveredNotifications(ids: string[]) {
  if (!Capacitor.isNativePlatform()) return;
  
  try {
    await PushNotifications.removeDeliveredNotifications({
      notifications: ids.map(id => ({ 
        id,
        title: '',
        body: '',
        data: {}
      }))
    });
  } catch (error) {
    console.error('[PushNotifications] Remove delivered error:', error);
  }
}

/**
 * Remove all delivered notifications
 */
export async function removeAllDeliveredNotifications() {
  if (!Capacitor.isNativePlatform()) return;
  
  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.error('[PushNotifications] Remove all delivered error:', error);
  }
}

// Handle notification received in foreground
function handleNotificationReceived(notification: any) {
  // Update unread count in query cache
  // This will trigger UI updates
  window.dispatchEvent(new CustomEvent('push-notification-received', {
    detail: notification
  }));
}

// Handle notification action (user tapped)
function handleNotificationAction(action: any) {
  const data = action.notification.data;
  
  // Navigate based on notification type
  if (data?.type === 'booking' && data?.bookingId) {
    window.location.href = `/reservas/${data.bookingId}`;
  } else if (data?.type === 'message' && data?.conversationId) {
    window.location.href = `/chat/${data.conversationId}`;
  } else if (data?.type === 'offer' && data?.offerId) {
    window.location.href = `/ofertas/nova?offerId=${data.offerId}`;
  } else {
    window.location.href = '/notificacoes';
  }
}

/**
 * Check if push notifications are enabled
 */
export async function arePushNotificationsEnabled(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  
  try {
    const status = await PushNotifications.checkPermissions();
    return status.receive === 'granted';
  } catch {
    return false;
  }
}

/**
 * Show local notification (for Realtime-triggered notifications)
 * 
 * ⚠️ NOTA: Quando Edge Function estiver deployada, FCM enviará push nativo.
 * Atualmente, sem Edge Function, esta função apenas loga (não mostra UI).
 * 
 * Para notificações em background, é necessário:
 * 1. Edge Function deployada com FCM_SERVICE_ACCOUNT_JSON
 * 2. Ou usar solução alternativa via suporte Lovable
 */
export async function showLocalNotification(
  title: string,
  message: string,
  data?: Record<string, any>
): Promise<void> {
  // Web: use standard notification API (se disponível)
  if (!Capacitor.isNativePlatform()) {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body: message,
        icon: '/apple-touch-icon.png',
        data: data,
      });
    }
    return;
  }

  // Android/iOS: Sem @capacitor/local-notifications, apenas logamos
  // O FCM push virá quando Edge Function estiver deployada
  console.log('[PushNotifications] Local notification (FCM not available):', { 
    title, 
    message, 
    data,
    note: 'Deploy Edge Function para notificações em background'
  });
  
  // Quando tiver Edge Function, descomente abaixo:
  // try {
  //   const { LocalNotifications } = await import('@capacitor/local-notifications');
  //   await LocalNotifications.schedule({
  //     notifications: [{
  //       title, body: message, id: Date.now(),
  //       schedule: { at: new Date() },
  //       sound: 'default', data: data || {},
  //     }]
  //   });
  // } catch (error) {
  //   console.error('[PushNotifications] Failed:', error);
  // }
}
